/**
 * Top-level pick function for the Three.js renderer.
 *
 * Combines:
 *   1. Spatial-index pre-filter (rbush bbox query around the click point
 *      expanded by tolerance) — drops 99 %+ of candidates in O(log N).
 *   2. AABB tolerance check against `entity.bounds` for each survivor —
 *      cheap reject before invoking analytic distance.
 *   3. Analytic distance via `distance.ts:distanceToRenderable` —
 *      produces the actual stroke-distance.
 *   4. Lowest-distance wins iff distance ≤ tolerance, with topmost tie-
 *      break (later zOrder beats earlier; matches AutoCAD pick rule).
 *
 * The tie-break iteration mirrors hitTest.ts:`findEntityAt` — we walk
 * candidates from topmost → bottommost (descending zOrder) and use a
 * strict less-than comparison so the first equal-distance entity wins,
 * which is the topmost one.
 */

import type { ChildGeometryFilter } from "../layerChainVisibility";
import type { RenderableEntity, SpatialIndex, Vec2 } from "../types";

import {
  distanceToRenderable,
  pointOutsideExpandedBounds,
} from "./distance";

/**
 * Pick the topmost entity within `toleranceWorld` of `world`, given an
 * explicit candidate set.
 *
 * Iteration order matches the SVG hit-test (`hitTest.ts:findEntityAt`):
 * the candidate array is walked from **last → first**, with a strict-
 * less-than comparison. Net effect: when two entities have exactly the
 * same distance to the probe point, the one **later** in the input
 * array wins — that's AutoCAD's "topmost" rule, since later array
 * entries paint on top in DRAWORDER.
 *
 * Callers that want a different ordering must sort the candidate array
 * before passing it in. `pick(world, …, spatialIndex)` (below) sorts
 * by zOrder ascending so DRAWORDER is preserved.
 */
export function pickFromCandidates(
  world: Vec2,
  toleranceWorld: number,
  candidates: ReadonlyArray<RenderableEntity>,
  /**
   * Codex PR #784 round 3 P2 — forwarded to `distanceToRenderable` so an
   * INSERT wrapper measures only its currently DRAWN children (see
   * `layerChainVisibility.ts makeVisibleChildGeometryFilter`). Omitted →
   * every child participates.
   */
  childFilter?: ChildGeometryFilter | null,
): RenderableEntity | null {
  return refinePickCandidates(world, toleranceWorld, candidates, childFilter)
    .hit;
}

/** Result of {@link refinePickCandidates}. */
export interface RefinedPickResult {
  /** The topmost candidate within tolerance (the {@link pickFromCandidates} answer). */
  readonly hit: RenderableEntity | null;
  /**
   * EVERY candidate whose analytic distance passed the SAME `<= tolerance`
   * test (and the same `childFilter`) as {@link hit} — i.e. everything
   * genuinely under the cursor, not merely inside a bbox the broad phase
   * returned. In the input array's order, filtered — never re-sorted.
   */
  readonly within: RenderableEntity[];
}

/**
 * The one distance-refinement walk behind {@link pickFromCandidates}, also
 * returning the FULL set of candidates that passed it.
 *
 * Codex PR #784 round 4 F3 — SELECTIONCYCLING used to list every broad-phase
 * (RBush bbox) survivor, so a click that hit a real entity while lying in the
 * empty interior of a sparse INSERT's large bbox correctly lost the pick for
 * the INSERT yet still OFFERED it as a stacked candidate: the cycling dialog
 * opened on geometry that is not under the cursor. The stacked list must be
 * built from {@link RefinedPickResult.within}, which is computed by the SAME
 * loop as the primary pick — each candidate's `distanceToRenderable` is
 * evaluated exactly ONCE.
 *
 * Iteration order and the strict `<` comparison are unchanged (see
 * {@link pickFromCandidates}), so `hit` is byte-identical to before.
 */
export function refinePickCandidates(
  world: Vec2,
  toleranceWorld: number,
  candidates: ReadonlyArray<RenderableEntity>,
  childFilter?: ChildGeometryFilter | null,
): RefinedPickResult {
  let bestEntity: RenderableEntity | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  const within: RenderableEntity[] = [];

  for (let i = candidates.length - 1; i >= 0; i -= 1) {
    const entity = candidates[i]!;
    if (pointOutsideExpandedBounds(world, entity.bounds, toleranceWorld)) {
      continue;
    }
    const d = distanceToRenderable(world, entity, childFilter);
    if (d === null || !Number.isFinite(d)) continue;
    if (d > toleranceWorld) continue;
    within.push(entity);
    if (d < bestDist) {
      bestDist = d;
      bestEntity = entity;
    }
  }
  return { hit: bestEntity, within };
}

/**
 * Spatial-index-backed pick. Hits rbush.intersect for the bbox prefilter,
 * then refines via `pickFromCandidates`.
 *
 * The intersect() output is unordered (rbush is a spatial tree, not a
 * sequence), so we sort by `(zOrder asc, id asc)` to recreate the
 * DRAWORDER order the SVG renderer iterates in. `pickFromCandidates`
 * then walks last→first, picking the topmost-by-zOrder.
 *
 * Returns the topmost entity id within tolerance, or `null` if no
 * entity is close enough.
 */
export function pick(
  world: Vec2,
  toleranceWorld: number,
  spatialIndex: SpatialIndex,
): string | null {
  const candidateIds = spatialIndex.intersect({
    left: world.x - toleranceWorld,
    top: world.y - toleranceWorld,
    right: world.x + toleranceWorld,
    bottom: world.y + toleranceWorld,
  });
  // Opt 3 — `intersect()` returns ids; resolve each through the
  // index's constructor-supplied entity resolver. Ids whose resolver
  // returns undefined (stale id between intersect and resolve) are
  // skipped silently — same behaviour as the pre-Opt-3 path which
  // would have seen a stub entity and rejected it at the analytic
  // distance step.
  const candidates: RenderableEntity[] = [];
  for (const id of candidateIds) {
    const entity = spatialIndex.resolve(id);
    if (entity) candidates.push(entity);
  }
  // Stable DRAWORDER order: ascending zOrder, then ascending id for
  // ties. pickFromCandidates iterates the array backwards so the last
  // (= top-most in DRAWORDER) entity wins exact-distance ties.
  candidates.sort((a, b) => {
    if (a.zOrder !== b.zOrder) return a.zOrder - b.zOrder;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  const hit = pickFromCandidates(world, toleranceWorld, candidates);
  return hit ? hit.id : null;
}
